import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useAtomValue } from 'jotai';

import { useGetBenchmarkingInsightsData } from '@/react-query/insights/useGetBenchmarkingInsightsData';

import { endDateAtom, startDateAtom } from '../../atoms';

import {
  useEnabledPilotRegionFilter,
  useMaterialityFilter,
  usePracticesFilter,
  useProviderRoleFilter,
  useTreatmentsFilter,
} from './clinicalFilters';

export const useGetFilteredClinicalData = ({
  isPilotRegionEnabled = true,
  isPracticesEnabled = true,
  isTreatmentsEnabled = true,
  isProviderRoleEnabled = true,
  isMaterialityEnabled = true,
}: {
  isPilotRegionEnabled?: boolean;
  isPracticesEnabled?: boolean;
  isTreatmentsEnabled?: boolean;
  isProviderRoleEnabled?: boolean;
  isMaterialityEnabled?: boolean;
}) => {
  const startDate = useAtomValue(startDateAtom);
  const endDate = useAtomValue(endDateAtom);

  const { data, isLoading } = useGetBenchmarkingInsightsData({
    start: startOfDay(startDate),
    end: endOfDay(endDate),
    priorStart: startOfDay(
      subDays(
        startDate.getTime() - (endDate.getTime() - startDate.getTime()),
        1,
      ),
    ),
    priorEnd: endOfDay(subDays(startDate, 1)),
  });

  // Apply filters (order doesn't matter)
  const filteredByPilotRegion = useEnabledPilotRegionFilter(
    data,
    isPilotRegionEnabled,
  );

  const filteredByPractices = usePracticesFilter(
    filteredByPilotRegion,
    isPracticesEnabled,
  );

  const filteredByTreatments = useTreatmentsFilter(
    filteredByPractices,
    isTreatmentsEnabled,
  );

  const filteredByProviderRole = useProviderRoleFilter(
    filteredByTreatments,
    isProviderRoleEnabled,
  );

  const filteredByMateriality = useMaterialityFilter(
    filteredByProviderRole,
    isMaterialityEnabled,
  );

  return { data: filteredByMateriality, isLoading };
};
